import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

/** The signed-in member's in-app notifications, newest first. */
export const listMyNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("notifications")
      .select("id, template, payload, status, created_at, read_at")
      .eq("recipient_id", context.userId)
      .eq("channel", "in_app")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Count of unread notifications — used for the badge in the account area. */
export const getUnreadNotificationCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { count, error } = await context.supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("recipient_id", context.userId)
      .eq("channel", "in_app")
      .is("read_at", null);
    if (error) return 0;
    return count ?? 0;
  });

export const markNotificationRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() } as never)
      .eq("id", data.id)
      .eq("recipient_id", context.userId)
      .is("read_at", null);
    return error
      ? { ok: false as const, error: error.message }
      : { ok: true as const };
  });

export const markAllNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() } as never)
      .eq("recipient_id", context.userId)
      .is("read_at", null);
    return error
      ? { ok: false as const, error: error.message }
      : { ok: true as const };
  });

/**
 * The member's inbox: the same messages Shields emailed them, rendered as
 * readable mail (subject, body, details, link) instead of raw payloads.
 */
export const listMyInbox = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { notificationContent } = await import("@/lib/email-templates/notification-content");
    const { data, error } = await context.supabase
      .from("notifications")
      .select("id, template, payload, created_at, read_at")
      .eq("recipient_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    const seen = new Set<string>();
    return (data ?? [])
      .filter((n) => {
        const key = `${n.template}:${n.created_at}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((n) => {
        const c = notificationContent(n.template, (n.payload ?? {}) as Record<string, unknown>);
        return {
          id: n.id,
          template: n.template,
          createdAt: n.created_at,
          readAt: n.read_at,
          subject: c.subject,
          title: c.title,
          body: c.body,
          details: c.details ?? [],
          ctaText: c.ctaText ?? null,
          ctaUrl: c.ctaUrl ?? null,
        };
      });
  });


export const getCommunicationPreferences = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await (context.supabase as any)
      .from("communication_preferences")
      .select("marketing_email, marketing_push, marketing_sms, service_reminders, opportunity_alerts")
      .eq("user_id", context.userId)
      .maybeSingle();
    return data ?? { marketing_email: false, marketing_push: false, marketing_sms: false, service_reminders: true, opportunity_alerts: true };
  });

export const updateCommunicationPreferences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({
    marketingEmail: z.boolean(),
    marketingPush: z.boolean(),
    marketingSms: z.boolean(),
    serviceReminders: z.boolean(),
    opportunityAlerts: z.boolean(),
  }).parse(value))
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any).from("communication_preferences").upsert({
      user_id: context.userId,
      marketing_email: data.marketingEmail,
      marketing_push: data.marketingPush,
      marketing_sms: data.marketingSms,
      service_reminders: data.serviceReminders,
      opportunity_alerts: data.opportunityAlerts,
      updated_at: new Date().toISOString(),
    });
    return error ? { ok: false as const, error: error.message } : { ok: true as const };
  });
